import type { CurrencyOptions } from '@procore/globalization-toolkit'
import { CurrencyFormatter } from '@procore/globalization-toolkit'
import { useId } from '@react-aria/utils'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import cx from 'classnames'
import React from 'react'
import type { CheckboxProps } from '../Checkbox'
import { Link as LinkElement } from '../Link'
import { tableSelectClassName } from '../Select/Select.styles'
import { Spinner } from '../Spinner'
import { useI18nContext } from '../_hooks/I18n'
import { mergeAriaIds } from '../_utils/a11y'
import { addSubcomponents } from '../_utils/addSubcomponents'
import { CellOverdueIconContext, OverdueContext } from './OverdueContext'
import {
  StyledCarat,
  StyledCaratWrapper,
  StyledCheckbox,
  StyledHead,
  StyledIconCell,
  StyledLoadingContainer,
  StyledOverdueIcon,
  StyledSelectCellWrapper,
  StyledTable,
  StyledTableBodyCell,
  StyledTableBodyRow,
  StyledTableCellDateInput,
  StyledTableCellInput,
  StyledTableCellSelect,
  StyledTableCellText,
  StyledTableCellTextArea,
  StyledTableGroupRow,
  StyledTableGroupRowContent,
  StyledTableGroupRowHeader,
  StyledTableGroupRowTitle,
  StyledTableHeaderCell,
  StyledTableHeaderCellIconWrapper,
  StyledTableHeaderCellLabel,
  StyledTableHeaderCellSortableContainer,
  StyledTableHeaderCellSortButton,
  StyledTableHeaderCellSortIcon,
  StyledTableInlineEditContainer,
  StyledTableInlineEditErrorBox,
  StyledTableWrapper,
} from './Table.styles'
import type {
  BodyCellProps,
  BodyProps,
  BodyRowProps,
  CaratProps,
  ContainerProps,
  CurrencyCellProps,
  DateSelectCellProps,
  GroupProps,
  GroupTitleProps,
  HeaderProps,
  HeaderRowProps,
  IconCellProps,
  InputCellProps,
  LinkCellProps,
  PercentCellProps,
  TableColumnSortOrder,
  TableHeaderCellProps,
  TableProps,
  TableSelectCellProps,
  TableTextCellProps,
  TextAreaCellProps,
} from './Table.types'

export const Table_ = React.forwardRef<
  HTMLTableElement,
  React.TableHTMLAttributes<HTMLTableElement> & TableProps
>(function Table({ children, inline, variant, ...props }, ref) {
  return (
    <StyledTable
      ref={ref}
      {...props}
      $inline={inline}
      $fixed={variant === 'fixed'}
    >
      {children}
    </StyledTable>
  )
})

export const Carat = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & CaratProps
>(function Carat({ expanded = false, 'aria-label': ariaLabel, ...props }, ref) {
  const I18n = useI18nContext()

  const defaultLabel = I18n.t(expanded ? 'collapse' : 'expand', {
    scope: 'core.table.a11y',
  })

  return (
    <StyledCaratWrapper
      ref={ref}
      aria-label={ariaLabel ?? defaultLabel}
      aria-expanded={expanded}
      variant="tertiary"
      size="sm"
      icon={<StyledCarat $expanded={expanded} />}
      {...props}
    />
  )
})

export const Container = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & ContainerProps
>(function Container({ children, shelf = false, ...props }, ref) {
  return (
    <StyledTableWrapper
      {...props}
      ref={ref}
      shadowStrength={0}
      $withShelf={shelf}
    >
      {children}
    </StyledTableWrapper>
  )
})

export const Header = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & HeaderProps
>(function Header({ children, ...props }, ref) {
  return (
    <StyledHead ref={ref} {...props}>
      {children}
    </StyledHead>
  )
})

export const HeaderRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & HeaderRowProps
>(function HeaderRow({ children, ...props }, ref) {
  return (
    <tr ref={ref} {...props}>
      {children}
    </tr>
  )
})

export const HeaderCell = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & TableHeaderCellProps
>(function HeaderCell(
  {
    sortOrder: sortOrder_,
    onClick,
    onKeyUp,
    onSortOrderChange: onSortOrderChange_,
    sortable,
    children,
    snugfit = false,
    variant,
    'aria-describedby': ariaDescribedByProp,
    ...props
  },
  ref
) {
  const I18n = useI18nContext()
  const descriptionId = useId()
  const sortOrder: TableColumnSortOrder | undefined =
    sortOrder_ ?? (variant !== 'snugfit' ? variant : undefined)
  const isSortable = sortOrder !== undefined && sortable !== false

  const ariaSortValues = {
    asc: 'ascending',
    desc: 'descending',
    '': 'none',
  } as const

  const onSortOrderChange = () => {
    if (!onSortOrderChange_) {
      return
    }

    const nextValue =
      sortOrder === '' ? 'asc' : sortOrder === 'asc' ? 'desc' : ''
    onSortOrderChange_(nextValue)
  }

  return (
    <StyledTableHeaderCell
      ref={ref}
      {...props}
      role="columnheader"
      aria-sort={isSortable ? ariaSortValues[sortOrder ?? ''] : undefined}
      aria-describedby={isSortable ? undefined : ariaDescribedByProp}
      $sortable={isSortable}
      $sortOrder={sortOrder}
      $snugfit={snugfit || variant === 'snugfit'}
      {...(isSortable
        ? {}
        : {
            onKeyUp: (e: React.KeyboardEvent<HTMLTableHeaderCellElement>) => {
              return onKeyUp?.(e)
            },
            onClick: (e: React.MouseEvent<HTMLTableHeaderCellElement>) => {
              return onClick?.(e)
            },
          })}
    >
      <StyledTableHeaderCellSortableContainer>
        {isSortable ? (
          <>
            <StyledTableHeaderCellSortButton
              type="button"
              aria-describedby={mergeAriaIds(
                descriptionId,
                ariaDescribedByProp
              )}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                onSortOrderChange()
                onClick?.(
                  e as unknown as React.MouseEvent<HTMLTableHeaderCellElement>
                )
              }}
              onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                onKeyUp?.(
                  e as unknown as React.KeyboardEvent<HTMLTableHeaderCellElement>
                )
              }}
            >
              <StyledTableHeaderCellLabel>
                {children}
              </StyledTableHeaderCellLabel>
              <StyledTableHeaderCellIconWrapper aria-hidden="true">
                <StyledTableHeaderCellSortIcon />
              </StyledTableHeaderCellIconWrapper>
            </StyledTableHeaderCellSortButton>
            <VisuallyHidden aria-hidden="true" id={descriptionId}>
              {I18n.t('core.table.sortableColumn')}
            </VisuallyHidden>
          </>
        ) : (
          <StyledTableHeaderCellLabel>{children}</StyledTableHeaderCellLabel>
        )}
      </StyledTableHeaderCellSortableContainer>
    </StyledTableHeaderCell>
  )
})

export const Body = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & BodyProps
>(function Body({ children, ...props }, ref) {
  return (
    <tbody ref={ref} {...props}>
      {children}
    </tbody>
  )
})

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox(props, ref) {
    const I18n = useI18nContext()

    return (
      <StyledCheckbox
        aria-label={I18n.t('core.table.checkboxLabel')}
        ref={ref}
        {...props}
      />
    )
  }
)

export const GroupTitle = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & GroupTitleProps
>(function GroupTitle({ children, ...props }, ref) {
  return (
    <StyledTableGroupRowTitle ref={ref} {...props}>
      {children}
    </StyledTableGroupRowTitle>
  )
})

export const Group = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & GroupProps
>(function Group({ children, colSpan = 1000, depth = false, ...props }, ref) {
  return (
    <StyledTableGroupRow ref={ref} $depth={depth} {...props}>
      <td colSpan={colSpan}>
        <StyledTableGroupRowContent>
          <StyledTableGroupRowHeader>{children}</StyledTableGroupRowHeader>
        </StyledTableGroupRowContent>
      </td>
    </StyledTableGroupRow>
  )
})

export const BodyRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & BodyRowProps
>(function BodyRow({ children, overdue = false, ...props }, ref) {
  const iconClaimedRef = React.useRef(false)
  iconClaimedRef.current = false

  const hasExplicitIcon = React.useMemo(
    () =>
      overdue &&
      React.Children.toArray(children).some(
        (child) => React.isValidElement(child) && child.props.overdueIcon
      ),
    [children, overdue]
  )

  const overdueContext = React.useMemo(
    () => ({ overdue, hasExplicitIcon, iconClaimedRef }),
    [overdue, hasExplicitIcon]
  )

  return (
    <OverdueContext.Provider value={overdueContext}>
      <StyledTableBodyRow ref={ref} $overdue={overdue} {...props}>
        {children}
      </StyledTableBodyRow>
    </OverdueContext.Provider>
  )
})

export const BodyCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & BodyCellProps
>(function BodyCell(
  { children, overdueIcon = false, snugfit = false, variant = '', ...props },
  ref
) {
  return (
    <CellOverdueIconContext.Provider value={overdueIcon}>
      <StyledTableBodyCell
        ref={ref}
        $snugfit={snugfit || variant === 'snugfit'}
        {...props}
      >
        {children}
      </StyledTableBodyCell>
    </CellOverdueIconContext.Provider>
  )
})

export const CurrencyCell = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CurrencyCellProps
>(function CurrencyCell({ children, value, currencyOptions, ...props }, ref) {
  const I18n = useI18nContext()
  const icon = useOverdueIcon(true)

  if (children) {
    return (
      <StyledTableCellText $currency ref={ref} {...props}>
        {icon}
        {children}
      </StyledTableCellText>
    )
  }

  const internalCurrencyOptions: CurrencyOptions = {
    locale: I18n.locale,
    currencyIsoCode: 'USD', // fallback to USD if currencyIsoCode is not provided
    ...currencyOptions,
  }
  const formatter = new CurrencyFormatter(internalCurrencyOptions)

  const internalValue = value ? formatter.formatCurrency(value) : null

  return (
    <StyledTableCellText $currency ref={ref} {...props}>
      {icon}
      {internalValue}
    </StyledTableCellText>
  )
})

export const DateSelectCell = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<DateSelectCellProps>
>(function DateSelectCell(
  {
    children,
    className,
    disabled = false,
    errorMessage,
    processing = false,
    ...props
  },
  ref
) {
  const error = Boolean(errorMessage)
  const errorId = useId()
  const showErrorMessage = !processing && error

  return (
    <StyledTableInlineEditContainer
      $disabled={disabled}
      $error={error}
      $size="block"
      className={cx({ disabled, error })}
    >
      {processing && (
        <StyledLoadingContainer>
          <Spinner loading={processing} size="md" />
        </StyledLoadingContainer>
      )}
      <StyledTableCellDateInput
        aria-invalid={props.error || error}
        aria-describedby={showErrorMessage ? errorId : undefined}
        {...props}
        disabled={processing || disabled}
        ref={ref}
        variant={error ? 'error' : ''}
        error={props.error || error}
      />
      {showErrorMessage && (
        <StyledTableInlineEditErrorBox id={errorId}>
          {errorMessage}
        </StyledTableInlineEditErrorBox>
      )}
    </StyledTableInlineEditContainer>
  )
})

export const IconCell = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & IconCellProps
>(function IconCell({ children, ...props }, ref) {
  return (
    <StyledIconCell ref={ref} {...props}>
      {children}
    </StyledIconCell>
  )
})

export const InputCell = React.forwardRef<HTMLInputElement, InputCellProps>(
  function InputCell(
    {
      disabled = false,
      errorMessage = '',
      processing = false,
      size = 'block', // TODO FIX THIS TYPE
      ...props
    },
    ref
  ) {
    const error = Boolean(errorMessage)
    const errorId = useId()
    const showErrorMessage = !processing && error

    return (
      <StyledTableInlineEditContainer
        $disabled={disabled}
        $error={error}
        $size={size}
        className={cx({ disabled, error })}
      >
        {processing && (
          <StyledLoadingContainer>
            <Spinner loading={processing} size="md" />
          </StyledLoadingContainer>
        )}
        <StyledTableCellInput
          aria-describedby={mergeAriaIds(
            props['aria-describedby'],
            showErrorMessage ? errorId : undefined
          )}
          {...props}
          ref={ref}
          error={props.error || error}
          disabled={processing || disabled}
        />
        {showErrorMessage && (
          <StyledTableInlineEditErrorBox $size={size} id={errorId}>
            {errorMessage}
          </StyledTableInlineEditErrorBox>
        )}
      </StyledTableInlineEditContainer>
    )
  }
)

export const LinkCell = React.forwardRef<
  HTMLDivElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement> & LinkCellProps
>(function LinkCell({ children, href = '', ...props }, ref) {
  return (
    <TextCell ref={ref}>
      <LinkElement href={href} {...props}>
        {children}
      </LinkElement>
    </TextCell>
  )
})

export const PercentCell = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & PercentCellProps
>(function PercentCell({ children, ...props }, ref) {
  const icon = useOverdueIcon(true)
  return (
    <StyledTableCellText $percent ref={ref} {...props}>
      {icon}
      {children}
    </StyledTableCellText>
  )
})

export const SelectCell = React.forwardRef<
  HTMLDivElement,
  TableSelectCellProps
>(function SelectCell(
  {
    block = true,
    children,
    disabled,
    errorMessage,
    className,
    processing = false,
    ...props
  },
  ref
) {
  const errorId = useId()
  const error = Boolean(errorMessage)
  const hasClearIcon = !disabled && !props.loading && Boolean(props.label)
  const showErrorMessage = !processing && error

  return (
    <StyledTableInlineEditContainer
      $disabled={disabled}
      $error={error}
      $size="block"
      className={cx({ disabled, error })}
    >
      {processing && (
        <StyledLoadingContainer>
          <Spinner loading={processing} size="md" />
        </StyledLoadingContainer>
      )}
      <StyledSelectCellWrapper>
        <StyledTableCellSelect
          aria-invalid={props.error || error}
          aria-describedby={mergeAriaIds(
            props['aria-describedby'],
            showErrorMessage ? errorId : undefined
          )}
          {...props}
          $placeholder={!props.label}
          $hasClearIcon={hasClearIcon}
          block={block}
          className={cx(tableSelectClassName, className)}
          disabled={processing || disabled}
          error={props.error || error}
          ref={ref}
        >
          {children}
        </StyledTableCellSelect>
      </StyledSelectCellWrapper>
      {showErrorMessage && (
        <StyledTableInlineEditErrorBox id={errorId}>
          {errorMessage}
        </StyledTableInlineEditErrorBox>
      )}
    </StyledTableInlineEditContainer>
  )
})

export const TextAreaCell = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & TextAreaCellProps
>(function TextAreaCell(
  {
    disabled,
    errorMessage = '',
    processing = false,
    resizable = false,
    ...props
  },
  ref
) {
  const errorId = useId()
  const textareaRef =
    (ref as React.RefObject<HTMLTextAreaElement>) ||
    React.createRef<HTMLTextAreaElement>()

  const setHeight = () => {
    if (textareaRef && textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }
  const error = Boolean(errorMessage)
  const showErrorMessage = !processing && error

  return (
    <StyledTableInlineEditContainer
      $disabled={disabled}
      $error={error}
      $size="block"
      className={cx({ disabled, error })}
    >
      {processing && (
        <StyledLoadingContainer>
          <Spinner loading={processing} size="md" />
        </StyledLoadingContainer>
      )}
      <StyledTableCellTextArea
        aria-invalid={error}
        aria-describedby={mergeAriaIds(
          props['aria-describedby'],
          showErrorMessage ? errorId : undefined
        )}
        {...props}
        $resizable={resizable}
        disabled={processing || disabled}
        onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
          setHeight()
          props.onBlur && props.onBlur(event)
        }}
        ref={textareaRef}
        resize="none"
        rows={1}
        error={error}
      />
      {showErrorMessage && (
        <StyledTableInlineEditErrorBox id={errorId}>
          {errorMessage}
        </StyledTableInlineEditErrorBox>
      )}
    </StyledTableInlineEditContainer>
  )
})

function useOverdueIcon(rightAligned?: boolean) {
  const { overdue, hasExplicitIcon, iconClaimedRef } =
    React.useContext(OverdueContext)
  const cellOverdueIcon = React.useContext(CellOverdueIconContext)
  const I18n = useI18nContext()

  if (!overdue) return null

  let show = false
  if (hasExplicitIcon) {
    show = cellOverdueIcon
  } else if (!iconClaimedRef.current) {
    iconClaimedRef.current = true
    show = true
  }

  if (!show) return null
  return (
    <StyledOverdueIcon
      aria-hidden={false}
      size="sm"
      role="img"
      aria-label={I18n.t('core.table.a11y.overdue')}
      $rightAligned={rightAligned}
    />
  )
}

export const TextCell = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & TableTextCellProps
>(function TextCell({ children, ...props }, ref) {
  const icon = useOverdueIcon()
  return (
    <StyledTableCellText ref={ref} {...props}>
      {children}
      {icon}
    </StyledTableCellText>
  )
})

Table_.displayName = 'Table'

Body.displayName = 'Table.Body'

BodyCell.displayName = 'Table.BodyCell'

BodyRow.displayName = 'Table.BodyRow'

Carat.displayName = 'Table.Carat'

Checkbox.displayName = 'Table.Checkbox'

Container.displayName = 'Table.Container'

CurrencyCell.displayName = 'Table.CurrencyCell'

DateSelectCell.displayName = 'Table.DateSelect'

Group.displayName = 'Table.Group'

GroupTitle.displayName = 'Table.GroupTitle'

Header.displayName = 'Table.Header'

HeaderCell.displayName = 'Table.HeaderCell'

HeaderRow.displayName = 'Table.HeaderRow'

IconCell.displayName = 'Table.IconCell'

InputCell.displayName = 'Table.InputCell'

LinkCell.displayName = 'Table.LinkCell'

PercentCell.displayName = 'Table.ParentCell'

TextAreaCell.displayName = 'Table.TextAreaCell'

TextCell.displayName = 'Table.TextCell'

/**

 @since 10.19.0

 @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_demos-table--demo)

 */
export const Table = addSubcomponents(
  {
    Body,
    BodyCell,
    BodyRow,
    Carat,
    Checkbox,
    Container,
    CurrencyCell,
    DateSelectCell,
    Group,
    GroupTitle,
    Header,
    HeaderCell,
    HeaderRow,
    IconCell,
    InputCell,
    LinkCell,
    PercentCell,
    SelectCell,
    TextAreaCell,
    TextCell,
  },
  Table_
)
